import { useEffect, useState, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  useLocation,
  generatePath,
  useOutletContext,
} from 'react-router-dom';
import {
  ButtonPseudo,
  SidePanel,
  SidePanelType,
  BlacklistUserInfo,
  ButtonPrimary,
  ButtonSecondary,
  Popup,
  PermissionEnum,
  PermissionDenied,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearBlacklistUserInfoState } from './store/slices/user';
import {
  useLazyGetBlacklistDataItemQuery,
  useDeleteBlacklistItemMutation,
} from './store/services';
import ROUTES from '../../routes';

import cn from './blacklist.module.css';

const BlacklistUserInfoHOC = ({ blacklist }: { blacklist: boolean }) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const {
    blacklist: {
      info: { info },
      item: { blacklist: blacklistData },
    },
    amlDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { userId, blocklistId, itemId } = useParams();

  const { getBlacklistAction } = useOutletContext<{
    getBlacklistAction?: (blacklistId: string) => void;
  }>();

  const { search } = useLocation();

  const [getBlacklistDataItem, { isLoading }] =
    useLazyGetBlacklistDataItemQuery();
  const [deleteBlacklistItem, { isLoading: deleteInProgress, isSuccess }] =
    useDeleteBlacklistItemMutation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getBlacklistDataItemAction = useCallback((id: string) => {
    getBlacklistDataItem({
      id: blocklistId || (itemId as string),
      dataId: id,
    });
  }, []);

  const onDelete = async () => {
    setDeleteError('');

    const response = (await deleteBlacklistItem({
      id: itemId as string,
      itemId: userId as string,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    }
  };

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setDeleteError('');

    let link = '';

    if (blacklist) {
      link = generatePath(ROUTES.BLACKLIST_ITEM, { itemId: itemId as string });
    } else {
      link = ROUTES.BLACKLIST_LIST_USERS;
    }

    setTimeout(() => {
      navigate(`${link}${search || ''}`);
    }, 100);
  }, []);

  const onEditClick = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        `${generatePath(ROUTES.BLACKLIST_ITEM_ADD_RECORD, {
          itemId: itemId as string,
        })}${search || ''}`,
        {
          state: {
            record: info,
          },
        },
      );
    }, 200);
  };

  useEffect(() => {
    if (!info && userId && (blocklistId || itemId)) {
      getBlacklistDataItemAction(userId);
    }
  }, [getBlacklistDataItemAction, userId, info, itemId]);

  useEffect(() => {
    if (isSuccess) {
      setDeleteError('');
      setDeletePopupVisible(false);
      handleClose();

      if (getBlacklistAction) {
        getBlacklistAction(itemId as string);
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearBlacklistUserInfoState());
    };
  }, [dispatch]);

  let content;

  if (activePermissions?.includes(PermissionEnum.BLACKLISTS_ITEMS_LIST)) {
    content = <BlacklistUserInfo isLoading={isLoading} info={info} />;
  } else {
    content = <PermissionDenied />;
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title="User details"
        footer={
          <>
            {blacklistData && !blacklistData?.source && (
              <>
                <ButtonPrimary
                  text="Duplicate Record"
                  onClick={onEditClick}
                  disabled={!info}
                  bold
                />
                {activePermissions?.includes(
                  PermissionEnum.BLACKLISTS_ITEMS_DELETE_ACTION,
                ) && (
                  <ButtonSecondary
                    text="Delete record"
                    className={`button-white button-warning ${cn.deleteRecordButton}`}
                    onClick={() => setDeletePopupVisible(true)}
                    disabled={!info}
                    bold
                  />
                )}
              </>
            )}
            <ButtonPseudo text="Close" onClick={handleClose} bold />
          </>
        }
        sidepanelExpandedKey="blacklist-user"
        withoutExpand
        expanded
      >
        {content}
      </SidePanel>
      <Popup
        title="Delete an record?"
        description="Are you sure you want to delete an entry from this list? This action cannot be undone."
        popupClassName={cn.deleteRecordPopup}
        onCancelClick={() => {
          setDeletePopupVisible(false);
          setDeleteError('');
        }}
        onOkClick={onDelete}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        isLoading={deleteInProgress}
        parent={document.getElementById('delete-popup-wrapper') as HTMLElement}
      >
        <div>
          {deleteError && <div className={cn.deleteError}>{deleteError}</div>}
        </div>
      </Popup>
      <div id="delete-popup-wrapper" />
    </>
  );
};

export default BlacklistUserInfoHOC;
